<style scoped></style>
<template>
  <div>
    <div class="demo-spin-article">
      <div class="row ts-mb-6">
        <div class="col-md-6">
          <label class="form-col-label control-label required">{{
            $t('paymentMethodMapping.paymentMethod')
            }}</label>
          <a-select v-model="model.payment_method_id" :options="paymentMethods" show-search option-filter-prop="label"
            class="ts-w-full" :class="{ 'is-invalid': errors.has('payment_method_id') }" placeholder="Select"
            @change="onChangePaymentMethod"></a-select>
          <div class="validate" v-if="errors.has('payment_method_id')">
            {{ errors.first('payment_method_id') }}
          </div>
        </div>
        <div class="col-md-6">
          <label class="form-col-label control-label">{{
            $t('paymentMethodMapping.currency')
            }}</label>
          <a-select v-model="model.currency_id" :options="currencies" show-search option-filter-prop="label"
            class="ts-w-full" :class="{ 'is-invalid': errors.has('currency_id') }" placeholder="Select" allowClear
            @change="getCoaSetup"></a-select>
          <div class="validate" v-if="errors.has('currency_id')">
            {{ errors.first('currency_id') }}
          </div>
        </div>
      </div>
      <!-- Bank section - only show for Bank Transfer payment method -->
      <div class="row ts-mb-6" v-if="isBankTransfer">
        <div class="col-md-6">
          <label class="form-col-label control-label required">{{
            $t('paymentMethodMapping.bank')
            }}</label>
          <a-select v-model="model.bank_id" :options="banks" show-search option-filter-prop="label" class="ts-w-full"
            :class="{ 'is-invalid': errors.has('bank_id') }" placeholder="Select" allowClear
            @change="onChangeBank"></a-select>
          <div class="validate" v-if="errors.has('bank_id')">
            {{ errors.first('bank_id') }}
          </div>
        </div>
        <div class="col-md-6">
          <label class="form-col-label control-label required">{{
            $t('paymentMethodMapping.bankAccountNumber')
            }}</label>
          <a-select v-model="model.bank_account_number" :options="bankAccounts" show-search option-filter-prop="label"
            class="ts-w-full" :class="{ 'is-invalid': errors.has('bank_account_number') }" placeholder="Select"
            allowClear :disabled="!model.bank_id"></a-select>
          <div class="validate" v-if="errors.has('bank_account_number')">
            {{ errors.first('bank_account_number') }}
          </div>
        </div>
      </div>
      <div class="row ts-mb-6">
        <div class="col-md-12">
          <label class="form-col-label control-label required">{{
            $t('paymentMethodMapping.chartOfAccount')
          }}</label>
          <a-select v-model="model.account_id" :options="coaSetupOptions" show-search option-filter-prop="label"
            class="ts-w-full" :class="{ 'is-invalid': errors.has('account_id') }" placeholder="Select"
            :loading="loading" allowClear></a-select>
          <div class="validate" v-if="errors.has('account_id')">
            {{ errors.first('account_id') }}
          </div>
        </div>
      </div>
      <div class="row ts-mb-6">
        <div class="col-md-12">
          <label class="form-col-label control-label">{{
            $t('paymentMethodMapping.remarks')
            }}</label>
          <textarea v-model="model.remarks" rows="2" class="form-control"
            :class="{ 'is-invalid': errors.has('remarks') }"></textarea>
          <div class="validate" v-if="errors.has('remarks')">
            {{ errors.first('remarks') }}
          </div>
        </div>
      </div>
      <div slot="footer">
        <div class="ts-flex ts-justify-end ts-space-x-2 ts-sh">
          <ts-button @click.prevent="() => $emit('cancel')" class="btn-gray" :disabled="loading">
            {{ $t('cancel') }}</ts-button>
          <ts-button color="primary" v-if="!isUpdate" outline :waiting="loading" @click.prevent="onSaveAddNew">
            {{ $t('saveAddNew') }}</ts-button>
          <ts-button color="primary" v-if="!isUpdate" :waiting="loading" @click.prevent="onSave">
            {{ $t('save') }}</ts-button>
          <ts-button v-if="isUpdate" color="primary" @click.prevent="onUpdate" :waiting="loading">
            {{ $t('update') }}</ts-button>
        </div>
      </div>
    </div>
    <Spin size="large" fix v-if="loading"> </Spin>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { isEmpty } from 'lodash'

export default {
  name: 'paymentMethodMappingForm',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      model: {
        payment_method_id: undefined,
        account_id: null,
        bank_id: undefined,
        bank_account_number: undefined,
        currency_id: undefined,
        remarks: null
      }
    }
  },
  computed: {
    ...mapState('accounting/paymentMethodMapping', ['edit_data', 'formViewDatas']),
    isUpdate() {
      return !isEmpty(this.edit_data)
    },
    isBankTransfer() {
      // Check if selected payment method is Bank Transfer (ID: 3)
      return this.model.payment_method_id === 3
    },
    paymentMethods() {
      if (this.formViewDatas['paymentMethods']) {
        return this.formViewDatas['paymentMethods'].map(el => ({
          value: el.payment_method_id,
          label: el.payment_method
        }))
      }
      return []
    },
    currencies() {
      if (this.formViewDatas['currencies']) {
        return this.formViewDatas['currencies'].map(el => ({
          value: el.currency_id,
          label: `${el.currency_description} | ${el.currency_code}`
        }))
      }
      return []
    },
    banks() {
      if (this.formViewDatas['banks']) {
        return this.formViewDatas['banks'].map(el => ({
          value: el.bank_id,
          label: el.bank_name,
          accounts: el.accounts
        }))
      }
      return []
    },
    bankAccountsList() {
      if (this.model.bank_id) {
        const selectedBank = this.banks.find(b => b.value === this.model.bank_id)
        return selectedBank && selectedBank.accounts ? selectedBank.accounts : []
      }
      return []
    },
    bankAccounts() {
      return this.bankAccountsList.map(el => ({
        value: el.account_number,
        label: `${el.account_number} - ${el.account_name}`
      }))
    },
    coaSetupOptions() {
      if (this.formViewDatas['coaSetups']) {
        return this.formViewDatas['coaSetups'].map(el => ({
          value: el.account_id,
          label: `${el.account_number} - ${el.account_name_en}${el.currency ? ' | ' + el.currency.currency_code : ''}`
        }))
      }
      return []
    }
  },
  created() {
    this.fetchResource()
  },
  methods: {
    ...mapActions('accounting/paymentMethodMapping', ['getFormViewModel']),
    async fetchResource() {
      this.loading = true
      await this.getFormViewModel({
        index: undefined,
        params: {}
      })
        .then(() => {
          this.setEditData()
        })
        .finally(() => {
          this.loading = false
        })
    },
    onChangePaymentMethod() {
      // Reset bank fields when payment method changes
      if (!this.isBankTransfer) {
        this.model.bank_id = undefined
        this.model.bank_account_number = undefined
      }
    },
    onChangeBank() {
      // Clear bank account number when bank changes
      this.model.bank_account_number = undefined
    },
    getCoaSetup() {
      this.loading = true
      this.getFormViewModel({
        index: 'coaSetups',
        params: {
          fnName: 'coaSetups',
          currency_id: this.model.currency_id
        }
      })
        .then(() => {
          // Data is stored in formViewDatas
        })
        .catch(error => {
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSaveAddNew() {
      this.errors = new Errors()
      this.loading = true
      this.$store
        .dispatch('accounting/paymentMethodMapping/store', this.model)
        .then(response => {
          this.notice({ type: 'success', text: response.message })
          this.clearInput()
          this.$emit('fetchData')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSave() {
      this.errors = new Errors()
      this.loading = true
      this.$store
        .dispatch('accounting/paymentMethodMapping/store', this.model)
        .then(response => {
          this.notice({ type: 'success', text: response.message })
          this.$emit('cancel')
          this.$emit('fetchData')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onUpdate() {
      this.errors = new Errors()
      this.loading = true
      this.$store
        .dispatch('accounting/paymentMethodMapping/update', {
          id: this.edit_data.mapping_id,
          data: this.model
        })
        .then(response => {
          this.notice({ type: 'success', text: response.message })
          this.$emit('cancel')
          this.$emit('fetchData')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async setEditData() {
      if (!isEmpty(this.edit_data)) {
        this.model.payment_method_id = this.edit_data.payment_method_id
        this.model.account_id = this.edit_data.account_id
        this.model.bank_id = this.edit_data.bank_id
        this.model.bank_account_number = this.edit_data.bank_account_number
        this.model.currency_id = this.edit_data.currency_id
        this.model.remarks = this.edit_data.remarks
      }
    },
    clearInput() {
      this.model.payment_method_id = undefined
      this.model.account_id = null
      this.model.bank_id = undefined
      this.model.bank_account_number = undefined
      this.model.currency_id = undefined
      this.model.remarks = null
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'PAYMENT METHOD MAPPING',
        desc: not.text
      })
    }
  }
}
</script>
